import { getConsent, hydrateConsent, subscribeConsent } from "@/lib/consent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Google Analytics 4 measurement ID. */
export const GA_MEASUREMENT_ID = "G-MZGFKQ2PLX";

/** PostHog project settings. Public client key, no secret handling required. */
export const POSTHOG = {
  token: "phc_tnEJKAMrw2sJ8geDNRLeUsQazr8XurYPpdRyg9BpRoQu",
  apiHost: "https://us.i.posthog.com",
} as const;

/** Class applied to anything PostHog session recording must not capture. */
export const NO_CAPTURE_CLASS = "ph-no-capture";

/** Every event the site is allowed to send. Misspellings fail the typecheck. */
export type AnalyticsEvent =
  | "cta_click"
  | "book_call_click"
  | "phone_call_click"
  | "phone_click"
  | "scan_cta_click"
  | "scan_start_click"
  | "packages_view"
  | "decision_sprint_view"
  | "contact_submit"
  | "email_capture"
  | "outbound_click"
  | "scroll_depth"
  | "calendly_open"
  | "price_table_click"
  | "insight_video_play"
  | "tool_start"
  | "tool_result"
  | "tool_gate_view"
  | "tool_gate_submit"
  | "tool_report_view"
  | "tool_pdf_click"
  | "tool_callout_click"
  | "tool_compare";

type EventParams = Record<string, string | number | boolean>;

type PostHogClient = {
  init: (token: string, options: Record<string, unknown>) => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
  set_config: (options: Record<string, unknown>) => void;
  startSessionRecording?: () => void;
};

let posthogClient: PostHogClient | null = null;
let posthogLoading = false;
let gaLoaded = false;
let started = false;
let scheduled = false;
let recordingStarted = false;

let lastPage: { path: string; title: string } | null = null;
const scrollSent = new Set<number>();

function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // Google expects the array-like arguments object, not a rest-parameter array.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function granted(): boolean {
  return getConsent() === "granted";
}

/**
 * Analytics only run on the production domain. Local development, Lovable
 * previews and project hosts stay out of the measured traffic entirely.
 */
function measurableHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "ajility.com.au" || host.endsWith(".ajility.com.au");
}

function loadGoogleAnalytics() {
  if (gaLoaded || typeof document === "undefined" || !measurableHost()) return;
  gaLoaded = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
}

async function loadPostHog() {
  if (posthogClient || posthogLoading || !measurableHost()) return;
  posthogLoading = true;
  try {
    const mod = await import("posthog-js");
    const client = (mod.default ?? mod) as unknown as PostHogClient;
    client.init(POSTHOG.token, {
      api_host: POSTHOG.apiHost,
      capture_pageview: false,
      autocapture: true,
      disable_session_recording: true,
      person_profiles: "identified_only",
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: `.${NO_CAPTURE_CLASS}, .${NO_CAPTURE_CLASS} *`,
      },
      persistence: "memory",
    });
    posthogClient = client;
    if (granted()) upgradePostHog();
    if (lastPage) capturePostHogPageView(lastPage.path, lastPage.title);
  } catch {
    // Analytics must never break the page.
  } finally {
    posthogLoading = false;
  }
}

/** Cookies and session replay only after the visitor accepts. */
function upgradePostHog() {
  if (!posthogClient || recordingStarted) return;
  recordingStarted = true;
  try {
    posthogClient.set_config({ persistence: "localStorage+cookie" });
    posthogClient.startSessionRecording?.();
  } catch {
    // Analytics must never break the page.
  }
}

function capturePostHogPageView(path: string, title: string) {
  posthogClient?.capture("$pageview", {
    $current_url: window.location.href,
    path,
    title,
  });
}

function sendPageView(path: string, title: string) {
  if (!measurableHost()) return;
  window.gtag?.("event", "page_view", {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
    send_to: GA_MEASUREMENT_ID,
  });
  capturePostHogPageView(path, title);
}

/**
 * Sets Consent Mode defaults, loads the cookieless measurement for everyone and
 * wires the site-wide listeners. Safe to call twice.
 */
export function initAnalytics(): void {
  if (scheduled || typeof window === "undefined") return;
  scheduled = true;
  const afterLoad = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(startAnalytics, { timeout: 2000 });
    } else {
      window.setTimeout(startAnalytics, 0);
    }
  };
  if (document.readyState === "complete") afterLoad();
  else window.addEventListener("load", afterLoad, { once: true });
}

function startAnalytics(): void {
  if (started) return;
  started = true;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) window.gtag = gtag;

  // Consent Mode defaults must be pushed before any tag loads.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });

  hydrateConsent();
  loadGoogleAnalytics();
  void loadPostHog();
  applyConsent();
  subscribeConsent(applyConsent);
  // Preserve the first page view that arrived before the deferred tags were ready.
  if (lastPage) {
    window.gtag?.("event", "page_view", {
      page_path: lastPage.path,
      page_title: lastPage.title,
      page_location: window.location.href,
      send_to: GA_MEASUREMENT_ID,
    });
  }

  installClickListener();
  installScrollListener();
}

function applyConsent() {
  if (granted()) {
    gtag("consent", "update", { analytics_storage: "granted" });
    upgradePostHog();
  } else {
    gtag("consent", "update", { analytics_storage: "denied" });
  }
}

/** Called on first load and on every client-side route change. */
export function trackPageView(path: string, title?: string): void {
  if (typeof window === "undefined") return;
  const pageTitle = title ?? document.title;
  const changed = lastPage?.path !== path;
  lastPage = { path, title: pageTitle };
  if (changed) scrollSent.clear();
  sendPageView(path, pageTitle);
}

/** Sends one event to Google Analytics and PostHog with identical parameters. */
export function trackEvent(event: AnalyticsEvent, params?: EventParams): void {
  if (typeof window === "undefined" || !measurableHost()) return;
  window.gtag?.("event", event, { send_to: GA_MEASUREMENT_ID, ...params });
  posthogClient?.capture(event, params);
}

function isExternal(url: URL): boolean {
  return url.origin !== window.location.origin && /^https?:$/.test(url.protocol);
}

function installClickListener() {
  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      const location = anchor.dataset.analyticsLocation ?? window.location.pathname;

      if (anchor.dataset.analyticsCta) {
        trackEvent("cta_click", {
          label: anchor.dataset.analyticsCta,
          location,
          destination: url.href,
        });
      }

      if (url.hostname.endsWith("calendly.com") && !url.pathname.includes("15-minute-phone-call")) {
        trackEvent("book_call_click", { location });
      }

      if (
        url.hostname === "diagnostic.ajility.com.au" ||
        (!isExternal(url) && url.pathname === "/scan")
      ) {
        trackEvent("scan_cta_click", { location });
      }

      if (isExternal(url)) {
        trackEvent("outbound_click", { url: url.href });
      }
    },
    true,
  );
}

function installScrollListener() {
  const thresholds = [50, 90];
  let ticking = false;

  const measure = () => {
    ticking = false;
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const percent = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
    for (const threshold of thresholds) {
      if (percent >= threshold && !scrollSent.has(threshold)) {
        scrollSent.add(threshold);
        trackEvent("scroll_depth", { percent: threshold });
      }
    }
  };

  window.addEventListener(
    "scroll",
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    },
    { passive: true },
  );
}
